/**
 * Intelligent Internal Linking Architecture
 * Enterprise-level strategic internal linking system
 */

export interface LinkSuggestion {
  url: string;
  title: string;
  context: string;
  priority: 'high' | 'medium' | 'low';
  type: 'hub' | 'related' | 'contextual' | 'conversion';
}

export interface TopicCluster {
  hub: {
    url: string;
    title: string;
    keywords: string[];
  };
  spokes: Array<{
    url: string;
    title: string;
    keywords: string[];
    linkToHub: boolean;
  }>;
}

/**
 * Define topic clusters for the site
 */
export const TOPIC_CLUSTERS: TopicCluster[] = [
  {
    hub: {
      url: '/services',
      title: 'Roofing Services',
      keywords: ['roofing services', 'roof repair', 'roof replacement']
    },
    spokes: [
      {
        url: '/residential-roofing',
        title: 'Residential Roofing',
        keywords: ['residential roof', 'home roofing'],
        linkToHub: true
      },
      {
        url: '/commercial-roofing',
        title: 'Commercial Roofing',
        keywords: ['commercial roof', 'business roofing'],
        linkToHub: true
      },
      {
        url: '/storm-damage',
        title: 'Storm Damage Repair',
        keywords: ['storm damage', 'emergency roofing'],
        linkToHub: true
      }
    ]
  },
  {
    hub: {
      url: '/roof-installations-and-materials',
      title: 'Roofing Materials & Installation',
      keywords: ['roofing materials', 'roof installation', 'shingles', 'metal roofing']
    },
    spokes: [
      {
        url: '/brands',
        title: 'Roofing Brands',
        keywords: ['GAF', 'Owens Corning', 'CertainTeed'],
        linkToHub: true
      },
      {
        url: '/design-your-roof',
        title: 'Roof Design Tool',
        keywords: ['roof design', 'visualizer'],
        linkToHub: true
      }
    ]
  },
  {
    hub: {
      url: '/locations',
      title: 'Service Areas',
      keywords: ['service areas', 'locations', 'coverage area']
    },
    spokes: [
      // All location pages would be here - generated dynamically
    ]
  },
  {
    hub: {
      url: '/resources',
      title: 'Roofing Resources',
      keywords: ['roofing guides', 'roof maintenance', 'roofing information']
    },
    spokes: [
      {
        url: '/resources/understanding-roofing-materials',
        title: 'Understanding Roofing Materials',
        keywords: ['roofing materials guide'],
        linkToHub: true
      },
      {
        url: '/resources/roof-replacement-process',
        title: 'Roof Replacement Process',
        keywords: ['roof replacement steps'],
        linkToHub: true
      },
      {
        url: '/frequently-asked-questions',
        title: 'Roofing FAQ',
        keywords: ['roofing questions', 'roof FAQ'],
        linkToHub: true
      }
    ]
  }
];

/**
 * High-value conversion pages that should receive strategic internal links
 */
export const CONVERSION_PAGES = [
  {
    url: '/contact',
    title: 'Contact Us',
    priority: 'high' as const,
    anchor_variations: [
      'contact us for a free estimate',
      'get your free consultation',
      'schedule your roofing assessment',
      'reach out to our team'
    ]
  },
  {
    url: '/book-inspection',
    title: 'Book Free Inspection',
    priority: 'high' as const,
    anchor_variations: [
      'book your free inspection',
      'schedule a roof inspection',
      'get your free roof assessment',
      'book your consultation'
    ]
  },
  {
    url: '/financing',
    title: 'Roofing Financing',
    priority: 'medium' as const,
    anchor_variations: [
      'explore financing options',
      'learn about payment plans',
      'roofing financing available'
    ]
  }
];

/**
 * Intelligent link suggestion engine
 */
export class InternalLinkingEngine {
  
  /**
   * Generate contextual link suggestions for a page
   */
  static generateLinkSuggestions(
    currentPage: string,
    content: string,
    pageType: 'blog' | 'service' | 'location' | 'resource',
    keywords: string[] = []
  ): LinkSuggestion[] {
    const suggestions: LinkSuggestion[] = [];
    
    // 1. Add hub page links (if this is a spoke page)
    const hubLinks = this.getHubPageLinks(currentPage, keywords);
    suggestions.push(...hubLinks);
    
    // 2. Add related spoke links (same cluster)
    const relatedLinks = this.getRelatedSpokeLinks(currentPage, keywords);
    suggestions.push(...relatedLinks);
    
    // 3. Add contextual content links
    const contextualLinks = this.getContextualLinks(content, currentPage);
    suggestions.push(...contextualLinks);
    
    // 4. Add strategic conversion links
    const conversionLinks = this.getConversionLinks(pageType, content);
    suggestions.push(...conversionLinks);
    
    // 5. Add location-specific links (if applicable)
    if (pageType === 'location') {
      const locationLinks = this.getLocationSpecificLinks(currentPage);
      suggestions.push(...locationLinks);
    }
    
    // Sort by priority and remove duplicates
    return this.prioritizeAndDedup(suggestions);
  }
  
  /**
   * Get hub page links for spoke pages
   */
  private static getHubPageLinks(currentPage: string, keywords: string[]): LinkSuggestion[] {
    const suggestions: LinkSuggestion[] = [];
    
    for (const cluster of TOPIC_CLUSTERS) {
      // Check if current page is a spoke in this cluster
      const isSpoke = cluster.spokes.some(spoke => spoke.url === currentPage);
      
      if (isSpoke) {
        suggestions.push({
          url: cluster.hub.url,
          title: cluster.hub.title,
          context: `Learn more about our complete ${cluster.hub.title.toLowerCase()}`,
          priority: 'high',
          type: 'hub'
        });
      }
    }
    
    return suggestions;
  }
  
  /**
   * Get related spoke links within the same cluster
   */
  private static getRelatedSpokeLinks(currentPage: string, keywords: string[]): LinkSuggestion[] {
    const suggestions: LinkSuggestion[] = [];
    
    for (const cluster of TOPIC_CLUSTERS) {
      const currentSpoke = cluster.spokes.find(spoke => spoke.url === currentPage);
      
      if (currentSpoke) {
        // Add other spokes in the same cluster
        cluster.spokes
          .filter(spoke => spoke.url !== currentPage)
          .slice(0, 3) // Limit to 3 related links
          .forEach(spoke => {
            suggestions.push({
              url: spoke.url,
              title: spoke.title,
              context: `Also explore our ${spoke.title.toLowerCase()} services`,
              priority: 'medium',
              type: 'related'
            });
          });
      }
    }
    
    return suggestions;
  }
  
  /**
   * Get contextual links based on content analysis
   */
  private static getContextualLinks(content: string, currentPage: string): LinkSuggestion[] {
    const suggestions: LinkSuggestion[] = [];
    const contentLower = content.toLowerCase();
    
    // Define content-based link opportunities
    const contextualMappings = [
      {
        triggers: ['storm damage', 'hail damage', 'wind damage', 'emergency'],
        url: '/storm-damage',
        title: 'Storm Damage Repair',
        context: 'Need emergency storm damage repair?'
      },
      {
        triggers: ['insurance claim', 'insurance adjuster', 'claim process'],
        url: '/resources/insurance-claim-process',
        title: 'Insurance Claim Process',
        context: 'Navigate your insurance claim with our guide'
      },
      {
        triggers: ['financing', 'payment plan', 'affordable roofing'],
        url: '/financing',
        title: 'Roofing Financing Options',
        context: 'Explore our flexible financing options'
      },
      {
        triggers: ['roof materials', 'shingles', 'metal roofing', 'roofing options'],
        url: '/roof-installations-and-materials',
        title: 'Roofing Materials Guide',
        context: 'Learn about different roofing material options'
      },
      {
        triggers: ['roof maintenance', 'roof care', 'extend roof life'],
        url: '/resources/roof-replacement-process',
        title: 'Roof Maintenance Guide',
        context: 'Learn how to maintain your roof properly'
      }
    ];
    
    for (const mapping of contextualMappings) {
      if (mapping.url !== currentPage && 
          mapping.triggers.some(trigger => contentLower.includes(trigger))) {
        suggestions.push({
          url: mapping.url,
          title: mapping.title,
          context: mapping.context,
          priority: 'medium',
          type: 'contextual'
        });
      }
    }
    
    return suggestions;
  }
  
  /**
   * Get strategic conversion links
   */
  private static getConversionLinks(pageType: string, content: string): LinkSuggestion[] {
    const suggestions: LinkSuggestion[] = [];
    
    // Add conversion links strategically based on page type and content
    for (const conversionPage of CONVERSION_PAGES) {
      // Always suggest contact/booking on service and location pages
      if ((pageType === 'service' || pageType === 'location') && 
          (conversionPage.url === '/contact' || conversionPage.url === '/book-inspection')) {
        suggestions.push({
          url: conversionPage.url,
          title: conversionPage.title,
          context: conversionPage.anchor_variations[0],
          priority: conversionPage.priority,
          type: 'conversion'
        });
      }
      
      // Suggest financing on high-cost service content
      if (conversionPage.url === '/financing' && 
          (content.toLowerCase().includes('replacement') || 
           content.toLowerCase().includes('new roof') ||
           content.toLowerCase().includes('expensive'))) {
        suggestions.push({
          url: conversionPage.url,
          title: conversionPage.title,
          context: conversionPage.anchor_variations[0],
          priority: conversionPage.priority,
          type: 'conversion'
        });
      }
    }
    
    return suggestions;
  }
  
  /**
   * Get location-specific links for location pages
   */
  private static getLocationSpecificLinks(currentPage: string): LinkSuggestion[] {
    const suggestions: LinkSuggestion[] = [];
    
    // Extract location from current page
    const locationMatch = currentPage.match(/\/locations\/(.+)/);
    if (locationMatch) {
      const location = locationMatch[1];
      
      // Add links to service pages with location context
      suggestions.push({
        url: '/residential-roofing',
        title: 'Residential Roofing Services',
        context: `Residential roofing services in ${location}`,
        priority: 'high',
        type: 'contextual'
      });
      
      suggestions.push({
        url: '/commercial-roofing',
        title: 'Commercial Roofing Services',
        context: `Commercial roofing solutions in ${location}`,
        priority: 'medium',
        type: 'contextual'
      });
    }
    
    return suggestions;
  }
  
  /**
   * Prioritize and remove duplicate suggestions
   */
  private static prioritizeAndDedup(suggestions: LinkSuggestion[]): LinkSuggestion[] {
    // Remove duplicates based on URL
    const uniqueSuggestions = suggestions.filter((suggestion, index, self) => 
      index === self.findIndex(s => s.url === suggestion.url)
    );
    
    // Sort by priority: high -> medium -> low
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    
    return uniqueSuggestions.sort((a, b) => 
      priorityOrder[b.priority] - priorityOrder[a.priority]
    );
  }
  
  /**
   * Generate anchor text variations for a link
   */
  static generateAnchorText(linkTitle: string, context: string): string[] {
    return [
      linkTitle,
      context,
      `Learn more about ${linkTitle.toLowerCase()}`,
      `Our ${linkTitle.toLowerCase()} services`,
      `Explore ${linkTitle.toLowerCase()}`
    ];
  }
}

/**
 * Component helper to inject intelligent internal links
 */
export const getIntelligentLinks = (
  currentPage: string,
  content: string,
  pageType: 'blog' | 'service' | 'location' | 'resource',
  keywords: string[] = [],
  maxLinks: number = 5
): LinkSuggestion[] => {
  return InternalLinkingEngine
    .generateLinkSuggestions(currentPage, content, pageType, keywords)
    .slice(0, maxLinks);
};